import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Menu, X, Instagram } from "lucide-react";
import LuminartLogo from "./LuminartLogo";
import { INSTAGRAM_URL } from "../campaigns";
import { useSiteText } from "../lib/content";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = ({ visible }) => {
  const [open, setOpen] = useState(false);
  const t = useSiteText();
  const LINKS = [
    { label: t("nav_about", "About"), id: "about" },
    { label: t("nav_services", "Services"), id: "services" },
    { label: t("nav_work", "Work"), id: "work" },
    { label: t("nav_contact", "Contact"), id: "contact" },
  ];

  return (
    <AnimatePresence>
      {visible && (
        <motion.nav
          data-testid="main-navbar"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-black/40 border-b border-white/5"
        >
          <div className="mx-auto flex items-center justify-between px-6 md:px-12 lg:px-16 h-20">
            {/* Home logo */}
            <button
              data-testid="nav-home-link"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center gap-3 group"
            >
              <LuminartLogo className="w-10 h-auto" glow={false} />
              <span className="font-heading text-sm tracking-[0.35em] uppercase text-white/90 group-hover:text-white transition-colors hidden sm:block">
                Luminart
              </span>
            </button>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-10">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-${l.id}-link`}
                  onClick={() => scrollTo(l.id)}
                  className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors duration-300"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-instagram-link"
                aria-label="Luminart Projection on Instagram"
                className="text-white/70 hover:text-white transition-colors duration-300"
              >
                <Instagram size={18} strokeWidth={1.5} />
              </a>
            </div>

            {/* Mobile toggle */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="md:hidden text-white"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>

          {/* Mobile menu */}
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="md:hidden overflow-hidden bg-black/80 backdrop-blur-lg border-t border-white/5"
              >
                <div className="flex flex-col px-6 py-6 gap-5">
                  {LINKS.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`nav-mobile-${l.id}-link`}
                      onClick={() => {
                        setOpen(false);
                        setTimeout(() => scrollTo(l.id), 350);
                      }}
                      className="text-left text-sm uppercase tracking-[0.2em] text-white/70 hover:text-white"
                    >
                      {l.label}
                    </button>
                  ))}
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="nav-mobile-instagram-link"
                    className="flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-white/70 hover:text-white"
                  >
                    <Instagram size={18} strokeWidth={1.5} /> Instagram
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.nav>
      )}
    </AnimatePresence>
  );
};

export default Navbar;
